import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.tsx'
import { useTranslation } from 'react-i18next'
import { SheetDescription } from '@/components/ui/sheet.tsx'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import useGlobalStore from '@/useGlobalStore.ts'
import dayjs from 'dayjs'
import { CalendarSync, CalendarX2, RefreshCcw } from 'lucide-react'
import { Capacitor } from '@capacitor/core'

interface AuthDialogProps {
	open: boolean
	onDismissed: (dismissed: boolean) => void
	isFetchingUser: boolean
}

const SubscriptionManageDialog = ({ open, onDismissed, isFetchingUser }: AuthDialogProps) => {
	const { t } = useTranslation()
	const { availableAppLanguages, appLanguageCode } = useGlobalStore()
	const { user } = useGlobalStore()

	const handleInteractionOutside = () => {
		onDismissed(true)
	}

	return (
		<Dialog open={open}>
			<DialogContent
				dir={availableAppLanguages[appLanguageCode]?.dir}
				className="px-3 pb-6 pt-0 w-[95%] rounded-lg"
				hideClose={true}
				onInteractOutside={handleInteractionOutside}>
				<VisuallyHidden.Root>
					<DialogTitle>{t('Manage your subscription')}</DialogTitle>
					<SheetDescription>{t('Manage or cancel your subscriptions')}</SheetDescription>
				</VisuallyHidden.Root>
				<div className="flex flex-col gap-3">
					<div className="rounded-lg p-3">
						<div className="flex flex-col gap-2 mt-3">
							<div className="flex flex-col gap-2">
								<span>{t('Current account type')}:</span>
								{user?.subscription_expires_at !== null &&
								dayjs.utc(user?.subscription_expires_at) > dayjs().utc() ? (
									<Badge className="flex justify-center min-h-7 w-2/3 rounded-md text-sm font-medium flex-col diagonal-stripes border-0 text-black">
										{t('Unlimited Access')}
									</Badge>
								) : (
									<Badge variant="secondary" className="flex justify-center text-sm h-7 w-1/2">
										{t('Free Account')}
									</Badge>
								)}
							</div>
							<div className="mt-2">
								<span className="me-2">{t('Payment provider')}:</span>
								<span className="font-bold">
									{user?.subscription_store === 'play_store'
										? 'Google Play'
										: user?.subscription_store === 'app_store'
											? 'App Store'
											: '-'}
								</span>
							</div>
							{user?.subscription_plan_identifier !== null && (
								<div className="flex items-center">
									<RefreshCcw className="w-5 h-5 me-2" />
									<span className="me-2">{t('Billing cycle')}:</span>
									<span className="font-bold">
										{t((user?.subscription_plan_identifier ?? '').replace(/[\d\-_]/g, ''))}
									</span>
								</div>
							)}
							{user?.subscription_expires_at !== null && (
								<div className="flex items-center">
									<CalendarSync className="w-5 h-5 me-2" />
									<span className="me-2">{t('Next billing date')}:</span>
									{user?.unsubscribed_at !== null
										? t('Cancelled')
										: dayjs.utc(user?.subscription_expires_at).local().format('YYYY/MM/DD')}
								</div>
							)}
							{user?.unsubscribed_at !== null && (
								<div className="flex items-center">
									<CalendarX2 className="w-5 h-5 me-2" />
									<span className="me-2">{t('Subscription expiration date')}:</span>
									{dayjs.utc(user?.unsubscribed_at).local().format('YYYY/MM/DD')}
								</div>
							)}
							{(user?.subscription_store === 'play_store' && Capacitor.getPlatform() === 'android') ||
							(user?.subscription_store === 'app_store' && Capacitor.getPlatform() === 'ios') ? (
								<Button asChild={true} className="w-full text-sm active:bg-secondary-foreground mt-3">
									<a target="_blank" href={user?.subscription_management_url ?? '#'}>
										{t('View and manage subscription')}
									</a>
								</Button>
							) : (
								<div className="text-gray-500 dark:text-gray-400">
									{t('Subscription can not be managed on this platform.')}
								</div>
							)}
							{isFetchingUser && (
								<div className="animate-pulse flex justify-center mt-2">
									{t('Updating subscription...')}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="flex justify-center items-center text-sm mt-1 select-none text-gray-400">
					<span className="cursor-pointer" onClick={() => onDismissed(true)}>
						{t('Close')}
					</span>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default SubscriptionManageDialog
